import { useCallback, useEffect, useMemo, useState } from 'react';
import { ExternalLink, Trash2, Share2, Lock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { ENTITIES, EntityKey } from '@/components/entities/registry';
import { OperationsView } from '@/components/operations/OperationsView';
import { ColumnDef } from '@/components/operations/types';
import { cn } from '@/lib/utils';
import {
  ApiSavedDashboard,
  ApiSavedView,
  deleteSavedDashboardApi,
  deleteSavedViewApi,
  listSavedDashboardsApi,
  listSavedViewsApi,
  updateSavedDashboardApi,
  updateSavedViewApi,
} from '@/lib/savedViewsApi';

export type SavedViewsTab = 'dashboard' | EntityKey;

export interface OpenTarget {
  tab: SavedViewsTab;
  viewId?: string;
  dashboardId?: string;
}

type Visibility = 'User' | 'Shared' | 'System';

interface Row {
  id: string;
  kind: 'view' | 'dashboard';
  entityKey?: EntityKey;
  name: string;
  ownerId: string;
  ownerEmail: string;
  isShared: boolean;
  isSystem: boolean;
  updatedAt?: string;
  typeLabel: string;
  visibility: Visibility;
  ownerLabel: string;
  updatedDisplay: string;
  updatedTs: number;
}

interface Props {
  onOpen: (target: OpenTarget) => void;
}

export function SavedViewsPage({ onOpen }: Props) {
  const { token, currentUser } = useAuth();
  const currentUserId: string =
    (currentUser as any)?._id ?? (currentUser as any)?.id ?? '';

  const [rows, setRows] = useState<Row[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setError(null);
      try {
        const [ops, wos, sos, dashes] = await Promise.all([
          listSavedViewsApi(token, 'operations'),
          listSavedViewsApi(token, 'work-orders'),
          listSavedViewsApi(token, 'sales-orders'),
          listSavedDashboardsApi(token),
        ]);
        if (cancelled) return;
        const all: Row[] = [];
        ops.forEach((v) => all.push(buildRow(v, 'view', 'operations', currentUserId)));
        wos.forEach((v) => all.push(buildRow(v, 'view', 'work-orders', currentUserId)));
        sos.forEach((v) => all.push(buildRow(v, 'view', 'sales-orders', currentUserId)));
        dashes.forEach((d) =>
          all.push(buildRow(d, 'dashboard', undefined, currentUserId))
        );
        setRows(all);
      } catch (err) {
        console.error('Failed to load saved views', err);
        if (!cancelled) setError('Could not load saved views.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, currentUserId]);

  const canMutate = useCallback(
    (row: Row) => !row.isSystem && row.ownerId === currentUserId,
    [currentUserId]
  );

  const handleOpen = useCallback(
    (row: Row) => {
      if (row.kind === 'dashboard') {
        localStorage.setItem('pp_v2_dashboard_active', row.id);
        onOpen({ tab: 'dashboard', dashboardId: row.id });
      } else if (row.entityKey) {
        onOpen({ tab: row.entityKey, viewId: row.id });
      }
    },
    [onOpen]
  );

  const handleToggleShared = useCallback(
    async (row: Row) => {
      if (!canMutate(row)) return;
      const next = !row.isShared;
      setRows((prev) =>
        prev.map((r) =>
          r.id === row.id && r.kind === row.kind
            ? { ...r, isShared: next, visibility: deriveVisibility(r.isSystem, next) }
            : r
        )
      );
      try {
        if (row.kind === 'view') {
          await updateSavedViewApi(token, row.id, { isShared: next });
        } else {
          await updateSavedDashboardApi(token, row.id, { isShared: next });
        }
      } catch (err) {
        console.error('Failed to toggle shared', err);
        setRows((prev) =>
          prev.map((r) =>
            r.id === row.id && r.kind === row.kind
              ? { ...r, isShared: !next, visibility: deriveVisibility(r.isSystem, !next) }
              : r
          )
        );
      }
    },
    [token, canMutate]
  );

  const handleDelete = useCallback(
    async (row: Row) => {
      if (!canMutate(row)) return;
      if (!confirm(`Delete "${row.name}"?`)) return;
      const snapshot = rows;
      setRows((prev) => prev.filter((r) => !(r.id === row.id && r.kind === row.kind)));
      try {
        if (row.kind === 'view') {
          await deleteSavedViewApi(token, row.id);
        } else {
          await deleteSavedDashboardApi(token, row.id);
        }
      } catch (err) {
        console.error('Failed to delete', err);
        setRows(snapshot);
      }
    },
    [token, canMutate, rows]
  );

  const columns: ColumnDef<Row>[] = useMemo(
    () => [
      {
        id: 'name',
        label: 'Name',
        accessor: (r) => r.name,
        sortValue: (r) => r.name.toLowerCase(),
        totalType: 'count',
        dataType: 'string',
      },
      {
        id: 'type',
        label: 'Type',
        accessor: (r) => r.typeLabel,
        sortValue: (r) => r.typeLabel,
        totalType: 'unique',
        dataType: 'enum',
      },
      {
        id: 'visibility',
        label: 'Visibility',
        accessor: (r) => r.visibility,
        sortValue: (r) => r.visibility,
        totalType: 'unique',
        dataType: 'enum',
        render: (r) => <VisibilityBadge value={r.visibility} />,
      },
      {
        id: 'owner',
        label: 'Owner',
        accessor: (r) => r.ownerLabel,
        sortValue: (r) => r.ownerLabel.toLowerCase(),
        totalType: 'unique',
        dataType: 'string',
      },
      {
        id: 'updated',
        label: 'Updated',
        accessor: (r) => r.updatedDisplay,
        sortValue: (r) => r.updatedTs,
        numericValue: (r) => r.updatedTs,
        totalType: 'count',
        dataType: 'date',
      },
      {
        id: 'actions',
        label: 'Actions',
        accessor: () => '',
        sortValue: () => '',
        totalType: 'count',
        render: (row) => {
          const mutate = canMutate(row);
          const isOwner = row.ownerId === currentUserId;
          return (
            <div className="inline-flex items-center gap-1">
              <button
                onClick={() => handleOpen(row)}
                title="Open"
                className="p-1.5 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100"
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => handleToggleShared(row)}
                disabled={!mutate}
                title={
                  row.isSystem
                    ? 'System views cannot be changed'
                    : !isOwner
                      ? 'Only the owner can change sharing'
                      : row.isShared
                        ? 'Make private'
                        : 'Share with team'
                }
                className={cn(
                  'p-1.5 rounded-md',
                  mutate
                    ? 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
                    : 'text-slate-300 cursor-not-allowed'
                )}
              >
                {row.isShared ? (
                  <Share2 className="w-3.5 h-3.5" />
                ) : (
                  <Lock className="w-3.5 h-3.5" />
                )}
              </button>
              <button
                onClick={() => handleDelete(row)}
                disabled={!mutate}
                title={
                  row.isSystem
                    ? 'System views cannot be deleted'
                    : !isOwner
                      ? 'Only the owner can delete'
                      : 'Delete'
                }
                className={cn(
                  'p-1.5 rounded-md',
                  mutate
                    ? 'text-slate-500 hover:text-rose-600 hover:bg-rose-50'
                    : 'text-slate-300 cursor-not-allowed'
                )}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        },
      },
    ],
    [canMutate, currentUserId, handleOpen, handleToggleShared, handleDelete]
  );

  const useData = useCallback(() => rows, [rows]);

  if (error) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 py-16 text-center text-rose-500 text-sm">
        {error}
      </div>
    );
  }

  return (
    <OperationsView<Row>
      columns={columns}
      useData={useData}
      savedViewsEntityKey="__saved_items__"
      defaultSort={[{ columnId: 'name', direction: 'asc' }]}
      entityLabel="saved items"
      showSavedViewPicker={false}
    />
  );
}

function buildRow(
  v: ApiSavedView | ApiSavedDashboard,
  kind: 'view' | 'dashboard',
  entityKey: EntityKey | undefined,
  currentUserId: string
): Row {
  const isSystem = !!v.isSystem;
  const isShared = !!v.isShared;
  const visibility = deriveVisibility(isSystem, isShared);
  const ts = v.updatedAt ? Date.parse(v.updatedAt) : NaN;
  const validTs = Number.isFinite(ts);
  return {
    id: v.id,
    kind,
    entityKey,
    name: v.name,
    ownerId: v.ownerId,
    ownerEmail: v.ownerEmail,
    isShared,
    isSystem,
    updatedAt: v.updatedAt,
    typeLabel: kind === 'dashboard' ? 'Dashboard' : ENTITIES[entityKey!].label,
    visibility,
    ownerLabel: v.ownerId === currentUserId ? 'You' : v.ownerEmail || '',
    updatedDisplay: validTs
      ? new Date(ts).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
      : '',
    updatedTs: validTs ? ts : 0,
  };
}

function deriveVisibility(isSystem: boolean, isShared: boolean): Visibility {
  if (isSystem) return 'System';
  if (isShared) return 'Shared';
  return 'User';
}

function VisibilityBadge({ value }: { value: Visibility }) {
  const classes =
    value === 'System'
      ? 'bg-amber-100 text-amber-800 border-amber-200'
      : value === 'Shared'
        ? 'bg-blue-50 text-blue-700 border-blue-200'
        : 'bg-slate-100 text-slate-600 border-slate-200';
  return (
    <span
      className={cn(
        'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border',
        classes
      )}
    >
      {value}
    </span>
  );
}
